import type { UIMessage } from "@ai-sdk/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { MessageActions } from "./MessageActions";
import styles from "./MessageBubble.module.css";

interface MessageBubbleProps {
	message: UIMessage;
	userImage?: string | null;
}

// Detect RTL languages (Hebrew, Arabic, etc.)
function isRTL(text: string): boolean {
	const rtlChars = /[\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC]/;
	return rtlChars.test(text);
}

// Decode HTML entities in text
function decodeHtmlEntities(text: string): string {
	return text
		.replace(/&#x2F;/g, "/")
		.replace(/&#x27;/g, "'")
		.replace(/&quot;/g, '"')
		.replace(/&amp;/g, "&")
		.replace(/&lt;/g, "<")
		.replace(/&gt;/g, ">");
}

export function MessageBubble({ message }: MessageBubbleProps) {
	const isUser = message.role === "user";
	const isAssistant = message.role === "assistant";

	// Extract text and check for AMA metadata from parts array (AI SDK v5 format)
	let content = "";
	let isAmaQuestion = false;

	for (const part of message.parts) {
		if (part.type === "text") {
			content += part.text;
			// Check if this part has AMA metadata
			// @ts-expect-error - custom metadata property
			if (part.isAmaQuestion) {
				isAmaQuestion = true;
			}
		}
	}

	// Decode HTML entities in the content
	const decodedContent = decodeHtmlEntities(content);
	const isContentRTL = isRTL(decodedContent);

	return (
		<div
			className={`${styles.container} ${isUser ? styles.userMessage : styles.assistantMessage}`}
		>
			<div
				className={`${styles.bubble} ${isUser && isAmaQuestion ? styles.amaQuestion : ""}`}
				dir={isContentRTL ? "rtl" : "ltr"}
			>
				{isAssistant ? (
					<>
						<div className={styles.markdown}>
							<ReactMarkdown remarkPlugins={[remarkGfm]}>
								{decodedContent}
							</ReactMarkdown>
						</div>
						<MessageActions content={decodedContent} />
					</>
				) : (
					<>
						{isAmaQuestion && (
							<img
								src="/images/ama-icon.png"
								alt="AMA"
								className={styles.amaIcon}
							/>
						)}
						<p className={styles.text}>{decodedContent}</p>
					</>
				)}
			</div>
		</div>
	);
}
